import Api from '@/api'
import { settingsStartDay, dayNameToIndex } from '@/utils/weekHelpers'

function normalizeZone(z) {
  if (!z) return z
  return {
    id: z.id,
    name: z.zone_name,
    projectId: z.project_id,
    companyId: z.company_id,
    status: z.is_active ? 'active' : 'inactive',
    address: '',
    geojson: z.geo_json,
    radius: z.radius,
    tradies: [],
    raw: z,
  }
}

// Dedupes concurrent getTimesheetSettings calls (e.g. multiple timesheet
// tabs mounting at once) so they share one in-flight request instead of all
// hitting the API before any of them has committed the cached state.
let settingsInFlight = null

export default {
  namespaced: true,

  state: {
    zones: [],
    zone: null,
    events: [],
    eventsMeta: null,
    // { current_page, last_page, per_page, total } from the last paged
    // GET /supervisor-timesheets; null after an unpaged (full-list) fetch.
    supervisorTimesheetsMeta: null,
    leaveEntries: [],
    alertSchedules: [],
    // null = not loaded yet, false = endpoint unavailable (old API — hide all
    // pay-period UI), object = loaded settings.
    settings: null,
    // Per-project pay-period overrides (BA-only). `list` mirrors the API's
    // projects[] — each { project_id, project_name, has_override, start_day,
    // pay_period_weeks, pay_period_anchor_date, current_period, override }.
    // `companyDefault` is the same shape minus the project fields. `loaded`
    // guards the one-time fetch. Consumers fall back to the company-wide
    // `settings` for any project not represented here.
    projectPayPeriods: { list: [], companyDefault: null, loaded: false },
  },

  getters: {
    zones(state) {
      return state.zones
    },
    zone(state) {
      return state.zone
    },
    supervisorTimesheetsMeta(state) {
      return state.supervisorTimesheetsMeta
    },
    timesheetSettings(state) {
      return state.settings
    },
    projectPayPeriods(state) {
      return state.projectPayPeriods.list
    },
    // Effective pay-period schedule for one project — its own override row
    // only when it actually has one; otherwise the live company-wide
    // `settings`. Reading `settings` (not the cached per-project
    // `companyDefault`) for the inherit case keeps every consumer current the
    // moment the company pay period is saved, without re-fetching the list.
    projectPayPeriod(state) {
      return (projectId) => {
        if (projectId != null) {
          // Callers pass project ids sourced from all over the app —
          // object-key iteration (Object.keys/group.projectId in
          // TimesheetListTab.vue) always coerces to a string, while
          // `projectPayPeriods.list` rows come straight from the API's JSON
          // (numeric project_id). Compare as strings so a project's override
          // still matches regardless of which shape the id arrives in.
          const row = state.projectPayPeriods.list.find(
            (p) => String(p.project_id) === String(projectId),
          )
          if (row && row.has_override) return row
        }
        return state.settings || state.projectPayPeriods.companyDefault
      }
    },
    // Week-start day index (JS getDay(): 0=Sun) for one project, honouring its
    // override. Falls back to the company start day, then Monday — so callers
    // that never loaded the project list keep today's behaviour.
    projectStartDay(state, getters) {
      return (projectId) => {
        const schedule = getters.projectPayPeriod(projectId)
        const idx = schedule ? dayNameToIndex(schedule.start_day) : null
        return idx == null ? settingsStartDay(state.settings) : idx
      }
    },
    projectOptions(_state, _getters, rootState, rootGetters) {
      const projects = rootState.project.projects || []
      const scoped = rootGetters['auth/isProjectScoped']
      const allowedIds = rootGetters['auth/supervisorProjectIds'] || []
      // `project.projects` is already scoped server-side to the user's role
      // (a supervisor's /projects only returns their assigned projects), so the
      // client-side id-filter is only a defensive narrowing. Apply it solely
      // when we actually have an id list — otherwise a tradie-supervisor whose
      // `supervisor_project_ids` came back empty on /auth/me (company_id not
      // resolved) would collapse the whole dropdown to blank.
      const visible =
        scoped && allowedIds.length > 0
          ? projects.filter((p) => allowedIds.includes(p.id))
          : projects
      // A scoped user with nothing to show would otherwise get an inexplicably
      // blank select (no "All Projects" entry either). Surface an explicit
      // disabled hint, distinguishing a failed fetch from an empty assignment.
      if (scoped && visible.length === 0) {
        return [
          {
            value: null,
            text: rootState.project.projectsError
              ? 'Couldn’t load projects - try again'
              : 'No projects assigned',
            disabled: true,
          },
        ]
      }
      // A scoped user with only one assigned project has nothing to toggle
      // between — keep today's plain single-project dropdown. 2+ projects
      // gets an "All (my projects)" entry; the API already scopes an omitted
      // project_id to the caller's own assignments for a supervisor tradie.
      return [
        ...(scoped
          ? visible.length > 1
            ? [{ value: null, text: 'All (my projects)' }]
            : []
          : [{ value: null, text: 'All Projects' }]),
        ...visible.map((p) => ({ value: p.id, text: p.name })),
      ]
    },
  },

  mutations: {
    setZones(state, zones) {
      state.zones = zones
    },

    setZone(state, zone) {
      state.zone = zone
    },
    addZone(state, zone) {
      state.zones.push(zone)
    },
    updateZoneInList(state, zone) {
      const i = state.zones.findIndex((z) => z.id === zone.id)
      if (i !== -1) state.zones.splice(i, 1, zone)
    },
    removeZone(state, id) {
      state.zones = state.zones.filter((z) => z.id !== id)
      if (state.zone && state.zone.id === id) state.zone = null
    },
    setEvents(state, events) {
      state.events = events
    },
    setEventsMeta(state, meta) {
      state.eventsMeta = meta
    },
    setSupervisorTimesheetsMeta(state, meta) {
      state.supervisorTimesheetsMeta = meta
    },
    setLeaveEntries(state, entries) {
      state.leaveEntries = entries
    },
    addLeaveEntry(state, entry) {
      state.leaveEntries.push(entry)
    },
    updateLeaveEntryInList(state, entry) {
      const i = state.leaveEntries.findIndex((e) => e.id === entry.id)
      if (i !== -1) state.leaveEntries.splice(i, 1, entry)
    },
    removeLeaveEntry(state, id) {
      state.leaveEntries = state.leaveEntries.filter((e) => e.id !== id)
    },
    SET_TIMESHEET_SETTINGS(state, settings) {
      state.settings = settings
    },
    SET_PROJECT_PAY_PERIODS(state, { list, companyDefault }) {
      state.projectPayPeriods = {
        list: list || [],
        companyDefault: companyDefault || null,
        loaded: true,
      }
    },
    UPSERT_PROJECT_PAY_PERIOD(state, entry) {
      if (!entry || entry.project_id == null) return
      const list = state.projectPayPeriods.list.slice()
      const i = list.findIndex((p) => p.project_id === entry.project_id)
      if (i === -1) list.push(entry)
      else list.splice(i, 1, { ...list[i], ...entry })
      state.projectPayPeriods = { ...state.projectPayPeriods, list }
    },
    setAlertSchedules(state, schedules) {
      state.alertSchedules = schedules
    },
    addAlertSchedule(state, schedule) {
      state.alertSchedules.push(schedule)
    },
    removeAlertSchedule(state, id) {
      state.alertSchedules = state.alertSchedules.filter((s) => s.id !== id)
    },
  },

  actions: {
    async getZones({ commit }, params = {}) {
      const res = await Api.get('/timesheet-zones', { params })
      const zones = (res.data.zones || []).map(normalizeZone)
      commit('setZones', zones)
      return zones
    },
    async getZone({ commit }, id) {
      const res = await Api.post('/timesheet-zones/show', { zone_id: id })
      const zone = normalizeZone(res.data.zone)
      commit('setZone', zone)
      return zone
    },

    async createZone({ commit }, payload) {
      const res = await Api.post('/timesheet-zones', payload)
      const zone = normalizeZone(res.data.zone)
      commit('addZone', zone)
      return zone
    },

    async updateZone({ commit }, payload) {
      const res = await Api.patch('/timesheet-zones', payload)
      const zone = normalizeZone(res.data.zone)
      commit('updateZoneInList', zone)
      commit('setZone', zone)
      return zone
    },

    async deleteZone({ commit }, id) {
      await Api.delete(`/timesheet-zones/${id}`)
      commit('removeZone', id)
    },

    async getEvents({ commit }, params = {}) {
      const res = await Api.get('/timesheet-events', { params })
      const events = res.data.events || []
      commit('setEvents', events)
      commit('setEventsMeta', res.data.meta || null)
      return events
    },

    // Business-admin standalone leave CRUD. No try/catch here — a 422
    // (overlap or approved-timesheet collision) or any other API error must
    // reach the caller with its response body intact so the component can
    // surface the API's human-written message rather than a generic one.
    async getLeaveEntries({ commit }, params = {}) {
      const res = await Api.get('/leave-entries', { params })
      const entries = res.data.data || []
      commit('setLeaveEntries', entries)
      return entries
    },

    async createLeaveEntry({ commit }, payload) {
      const res = await Api.post('/leave-entries', payload)
      const entry = res.data.data
      commit('addLeaveEntry', entry)
      return entry
    },

    // Batched sibling of createLeaveEntry: one request applying the same
    // leave range/type to several worker_ids, instead of the client firing
    // one POST /leave-entries per worker. Returns the per-worker results
    // array (each { worker_id, status: 'created'|'failed', data?, message? })
    // so the caller can report per-worker success/failure same as before.
    async createLeaveEntryBulk({ commit }, payload) {
      const res = await Api.post('/leave-entries/bulk', payload)
      const results = res.data.results || []
      results
        .filter((r) => r.status === 'created')
        .forEach((r) => commit('addLeaveEntry', r.data))
      return results
    },

    async updateLeaveEntry({ commit }, { id, ...fields }) {
      const res = await Api.put(`/leave-entries/${id}`, fields)
      const entry = res.data.data
      commit('updateLeaveEntryInList', entry)
      return entry
    },

    async deleteLeaveEntry({ commit }, id) {
      await Api.delete(`/leave-entries/${id}`)
      commit('removeLeaveEntry', id)
    },

    async getProjectAttendance(_, projectId) {
      const res = await Api.get(
        '/supervisor/projects/' + projectId + '/attendance',
      )
      return res.data
    },

    // Batched sibling of getProjectAttendance: one request covering every
    // project passed in (or every project the caller can see, if omitted),
    // instead of the client firing one attendance request per project.
    async getAttendance(_, projectIds) {
      const params = {}
      if (Array.isArray(projectIds) && projectIds.length) {
        params.project_ids = projectIds
      }
      const res = await Api.get('/supervisor/attendance', { params })
      return res.data
    },

    async approveEvent(_, { eventId, decision, adminComment }) {
      const body = { status: decision }
      if (adminComment && adminComment.trim())
        body.admin_comment = adminComment.trim()
      const res = await Api.patch(`/timesheet-events/${eventId}`, body)
      return res.data.event
    },

    // Bulk sibling of approveEvent — one PATCH covering every event id
    // instead of the client firing one request per event (plus a second
    // confirm call for pending_confirmation entries, folded server-side).
    // Returns { approved: number[], failed: [{id, reason}] } so callers can
    // surface partial failures instead of a binary success/error toast.
    async approveEventsBulk(_, { eventIds, decision, adminComment }) {
      const body = { event_ids: eventIds, status: decision }
      if (adminComment && adminComment.trim())
        body.admin_comment = adminComment.trim()
      const res = await Api.patch('/timesheet-events/bulk-status', body)
      return res.data
    },

    async manualClockIn(_, payload) {
      const res = await Api.post('/timesheet-events', payload)
      return res.data.event
    },

    // ── Tradie self-service (web parity with Flutter's clock in/out) ──
    // These hit the same tradie-facing, permission-free endpoints Flutter
    // already uses (token.access only, no BA timesheets.* permission).

    async getMyTimesheetProjects() {
      const res = await Api.get('/timesheet-projects/mine')
      return (res.data.data && res.data.data.projects) || []
    },

    async getMyTimesheetStatus() {
      const res = await Api.get('/timesheet-zones/my-status')
      return res.data.clocked_in || null
    },

    async getMyTimesheetHistory() {
      const res = await Api.get('/timesheet-zones/my-history')
      return res.data.data || []
    },

    async clockInSelf(_, payload) {
      const res = await Api.post('/timesheet-zones/clock-in', payload)
      return res.data.event
    },

    async clockOutSelf(_, payload) {
      const isMultipart = payload instanceof FormData
      const res = await Api.post('/timesheet-zones/clock-out', payload, {
        headers: isMultipart
          ? { 'Content-Type': 'multipart/form-data' }
          : undefined,
      })
      return res.data.event
    },

    async myManualEntry(_, payload) {
      const isMultipart = payload instanceof FormData
      const res = await Api.post('/timesheet-events/my', payload, {
        headers: isMultipart
          ? { 'Content-Type': 'multipart/form-data' }
          : undefined,
      })
      return res.data.event
    },

    // PHP only parses multipart bodies on POST — a multipart PUT arrives with
    // empty fields/files server-side. When photos are involved, POST with
    // Laravel's `_method` spoof instead (mirrors Flutter's updateMyEntry).
    // `data` may be a plain object or a FormData instance.
    async myEditEvent(_, { id, data }) {
      const isMultipart = data instanceof FormData
      if (isMultipart) {
        data.append('_method', 'PUT')
        const res = await Api.post(`/timesheet-events/my/${id}`, data, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        return res.data.event
      }
      const res = await Api.put(`/timesheet-events/my/${id}`, data)
      return res.data.event
    },

    async myDeleteEvent(_, id) {
      await Api.delete(`/timesheet-events/my/${id}`)
    },

    async manualBulkEntry(_, payload) {
      const res = await Api.post('/timesheet-events/bulk', payload)
      return res.data
    },

    async adminClockOut(_, { eventId, ...fields }) {
      const res = await Api.patch(
        `/timesheet-events/${eventId}/clock-out`,
        fields,
      )
      return res.data.event
    },

    async editEvent(_, { id, ...fields }) {
      const res = await Api.put(`/timesheet-events/${id}`, fields)
      return res.data.event
    },

    // Human confirmation step a pending_confirmation event needs before its
    // hours count toward payroll — see App\Support\TimesheetEventHours and
    // docs/designs/future-date-timesheet-entries.md.
    async confirmEvent(_, eventId) {
      const res = await Api.post(`/timesheet-events/${eventId}/confirm`)
      return res.data.event
    },

    async deleteEvent(_, id) {
      await Api.delete(`/timesheet-events/${id}`)
    },

    async getAlertSchedules({ commit }) {
      const res = await Api.get('/timesheet-alerts/schedules')
      commit('setAlertSchedules', res.data.schedules || [])
    },

    async createAlertSchedule({ commit }, payload) {
      const res = await Api.post('/timesheet-alerts/schedules', payload)
      commit('addAlertSchedule', res.data.schedule)
      return res.data.schedule
    },

    async deleteAlertSchedule({ commit }, id) {
      await Api.delete(`/timesheet-alerts/schedules/${id}`)
      commit('removeAlertSchedule', id)
    },

    // Pay-period settings. Never rejects: on any failure (404 on an old API,
    // network error) it commits `false` so callers fall back to legacy
    // behaviour and all pay-period UI stays hidden. Cached after first load;
    // pass { force: true } to refetch.
    async getTimesheetSettings({ state, commit }, { force = false } = {}) {
      if (!force && state.settings !== null) return state.settings
      if (!force && settingsInFlight) return settingsInFlight

      settingsInFlight = (async () => {
        try {
          const res = await Api.get('/timesheet-settings')
          const settings = (res.data && res.data.data) || false
          commit('SET_TIMESHEET_SETTINGS', settings)
          return settings
        } catch (e) {
          commit('SET_TIMESHEET_SETTINGS', false)
          return false
        }
      })()

      try {
        return await settingsInFlight
      } finally {
        settingsInFlight = null
      }
    },

    async updateTimesheetSettings({ commit }, payload) {
      const res = await Api.put('/timesheet-settings', payload)
      const settings = res.data && res.data.data
      if (settings) commit('SET_TIMESHEET_SETTINGS', settings)
      return settings
    },

    // Per-project pay-period overrides (BA-only). Like getTimesheetSettings
    // this never rejects on a 403/404 (old API, or a non-BA caller) — it
    // commits an empty list so consumers cleanly fall back to the company
    // schedule. Cached after the first load; pass { force: true } to refetch.
    async getProjectPayPeriods(
      { state, commit, rootState },
      { force = false } = {},
    ) {
      if (!force && state.projectPayPeriods.loaded) {
        return state.projectPayPeriods.list
      }
      // Skip the request entirely for non-BA callers (supervisors, etc.) —
      // the endpoint is BA-only and always 403s for them, which just spams
      // the console with a failed-request error for no benefit.
      if (
        rootState.auth.user &&
        rootState.auth.user.user_type !== 'business_admin'
      ) {
        commit('SET_PROJECT_PAY_PERIODS', { list: [], companyDefault: null })
        return []
      }
      try {
        const res = await Api.get('/timesheet-settings/projects')
        const data = (res.data && res.data.data) || {}
        commit('SET_PROJECT_PAY_PERIODS', {
          list: data.projects || [],
          companyDefault: data.company_default || null,
        })
        return state.projectPayPeriods.list
      } catch (e) {
        commit('SET_PROJECT_PAY_PERIODS', { list: [], companyDefault: null })
        return []
      }
    },

    async saveProjectPayPeriod(
      { commit },
      { projectId, payPeriodWeeks, payPeriodAnchorDate },
    ) {
      const res = await Api.put(`/timesheet-settings/projects/${projectId}`, {
        pay_period_weeks: payPeriodWeeks,
        pay_period_anchor_date: payPeriodAnchorDate,
      })
      const entry = res.data && res.data.data
      if (entry) commit('UPSERT_PROJECT_PAY_PERIOD', entry)
      return entry
    },

    async clearProjectPayPeriod({ commit }, projectId) {
      const res = await Api.delete(`/timesheet-settings/projects/${projectId}`)
      const entry = res.data && res.data.data
      if (entry) commit('UPSERT_PROJECT_PAY_PERIOD', entry)
      return entry
    },

    // Pass `page`/`per_page` for one page (meta lands in
    // state.supervisorTimesheetsMeta); omit both for every matching sheet —
    // the API only paginates when one of them is present, which is what the
    // CSV/PDF export relies on to get the full filtered set.
    async getSupervisorTimesheets({ commit }, params = {}) {
      const res = await Api.get('/supervisor-timesheets', { params })
      const paged = params.page != null || params.per_page != null
      if (paged) commit('setSupervisorTimesheetsMeta', res.data.meta || null)
      return res.data.data || []
    },

    async getSupervisorTimesheetSingle(_, id) {
      const res = await Api.get(`/supervisor-timesheets/${id}`)
      return res.data.data
    },

    async approveSupervisorTimesheet(_, id) {
      const res = await Api.post(`/supervisor-timesheets/${id}/approve`)
      return res.data.data
    },

    // Approved sheets only — re-opens the sheet (back to 'submitted') so the
    // supervisor can edit and resubmit; the API also reverts the crew's clock
    // events. The API rejects recall on any non-approved sheet.
    async recallSupervisorTimesheet(_, id) {
      const res = await Api.post(`/supervisor-timesheets/${id}/recall`)
      return res.data.data
    },

    async rejectSupervisorTimesheet(
      _,
      { id, rejectionReason, rejectionComment },
    ) {
      const res = await Api.post(`/supervisor-timesheets/${id}/reject`, {
        rejection_reason: rejectionReason,
        rejection_comment: rejectionComment || null,
      })
      return res.data.data
    },

    async updateSupervisorTimesheet(
      _,
      { id, rows, plantEntries, comment, updatedAt },
    ) {
      const body = { rows }
      // Omit entirely (not []) unless the user actually touched crew plant
      // this session — the API treats an absent key as "leave untouched" and
      // an explicit [] as "clear everything", so sending [] on an hours-only
      // save would wipe plant data the user never looked at.
      if (plantEntries !== undefined) body.plant_entries = plantEntries
      // Same omit-vs-send rule as plant_entries — the API preserves the
      // existing comment when this key is absent, so an undefined comment
      // (e.g. a caller that never touched the field) never wipes it.
      if (comment !== undefined) body.supervisor_comment = comment
      // Optimistic-concurrency token — see _assertNotStale on the API side.
      // Only meaningful once a draft became editable by someone other than
      // its owner; omitted entirely for callers that don't have it.
      if (updatedAt !== undefined) body.updated_at = updatedAt
      const res = await Api.put(`/supervisor-timesheets/${id}`, body)
      return res.data.data
    },

    // Draft-only — the API rejects submit on any non-draft sheet. Lets a BA
    // take over and lock in someone else's unfinished draft (see
    // timesheets.edit_drafts). Same payload shape as updateSupervisorTimesheet.
    async submitSupervisorTimesheetDraft(
      _,
      { id, rows, plantEntries, comment, updatedAt },
    ) {
      const body = { rows }
      if (plantEntries !== undefined) body.plant_entries = plantEntries
      if (comment !== undefined) body.supervisor_comment = comment
      if (updatedAt !== undefined) body.updated_at = updatedAt
      const res = await Api.post(`/supervisor-timesheets/${id}/submit`, body)
      return res.data.data
    },

    // Drafts only — the API rejects deletes on submitted/reviewed sheets.
    async deleteSupervisorTimesheet(_, id) {
      await Api.delete(`/supervisor-timesheets/${id}`)
    },

    async supervisorFetchProjects() {
      const res = await Api.get('/supervisor/projects')
      return res.data.data || []
    },

    async supervisorFetchTimesheet(_, { projectId, weekStart }) {
      const res = await Api.get('/supervisor/timesheets', {
        params: { project_id: projectId, week_start: weekStart },
      })
      return res.data.data
    },

    async supervisorSaveDraft(_, { id, rows, comment, plantEntries }) {
      const body = { rows, supervisor_comment: comment }
      // Omitted (not sent) unless the user actually touched crew plant this
      // session — the API treats an absent key as "leave untouched" and an
      // explicit [] as "clear everything" (see updateSupervisorTimesheet).
      if (plantEntries !== undefined) body.plant_entries = plantEntries
      const res = await Api.put(`/supervisor/timesheets/${id}`, body)
      return res.data.data
    },

    async supervisorUploadPhotos(_, { id, files }) {
      const formData = new FormData()
      files.forEach((file, i) => {
        formData.append(`photos[${i}]`, file, file.name)
      })
      const res = await Api.post(
        `/supervisor/timesheets/${id}/photos`,
        formData,
      )
      return res.data.photo_urls
    },

    async supervisorDeletePhoto(_, { id, mediaId }) {
      const formData = new FormData()
      formData.append('photos_to_delete[0]', mediaId)
      const res = await Api.post(
        `/supervisor/timesheets/${id}/photos`,
        formData,
      )
      return res.data.photo_urls
    },

    async supervisorSubmit(_, { id, rows, comment, plantEntries }) {
      const body = { rows, comment }
      if (plantEntries !== undefined) body.plant_entries = plantEntries
      const res = await Api.post(`/supervisor/timesheets/${id}/submit`, body)
      return res.data.data
    },

    async supervisorResubmit(_, { id, rows, comment, plantEntries }) {
      const body = { rows, supervisor_comment: comment }
      if (plantEntries !== undefined) body.plant_entries = plantEntries
      const res = await Api.post(`/supervisor/timesheets/${id}/resubmit`, body)
      return res.data.data
    },

    async supervisorFetchHistory() {
      const res = await Api.get('/supervisor/timesheets', {
        params: { mine: 1 },
      })
      return res.data.data || []
    },

    async supervisorExportHistory(_, { ids, format }) {
      return Api.post(
        '/supervisor/timesheets/export',
        { ids, format },
        { responseType: 'blob' },
      )
    },

    async supervisorFetchSingle(_, id) {
      const res = await Api.get(`/supervisor/timesheets/${id}`)
      return res.data.data
    },

    // Drafts only — the API rejects deletes on submitted/reviewed sheets.
    async supervisorDeleteTimesheet(_, id) {
      await Api.delete(`/supervisor/timesheets/${id}`)
    },

    async supervisorAddWorker(_, { projectId, name, email }) {
      const res = await Api.post(`/supervisor/projects/${projectId}/workers`, {
        name,
        email,
      })
      return res.data.data
    },

    // ── Tradie's own weekly timesheet (/my-week-sheets) ─────────────────────
    // Projects whose company has weekly timesheets on; empty hides the page.
    async myWeekProjects() {
      const res = await Api.get('/my-week-sheets/projects')
      return res.data.data || []
    },

    // The project's week start and plant switch, as the app reads them. Never
    // rejects: null falls back to a Monday week with plant shown.
    async myWeekSettings(_, projectId) {
      try {
        const res = await Api.get('/timesheet-settings', {
          params: { project_id: projectId },
        })
        return res.data.data || null
      } catch (e) {
        return null
      }
    },

    // Opens (or creates) the draft for a project-week. The API snaps the week
    // to the company's week start, so read week_start back from the result.
    async myWeekOpen(_, { projectId, weekStart }) {
      const res = await Api.post('/my-week-sheets', {
        project_id: projectId,
        week_start: weekStart,
      })
      return res.data.data
    },

    async myWeekFetch(_, id) {
      const res = await Api.get(`/my-week-sheets/${id}`)
      return res.data.data
    },

    async myWeekHistory() {
      const res = await Api.get('/my-week-sheets')
      return res.data.data || []
    },

    async myWeekSave(_, { id, body }) {
      const res = await Api.put(`/my-week-sheets/${id}`, body)
      return res.data.data
    },

    async myWeekUploadPhotos(_, { id, files }) {
      const formData = new FormData()
      files.forEach((file, i) => {
        formData.append(`photos[${i}]`, file, file.name)
      })
      const res = await Api.post(`/my-week-sheets/${id}/photos`, formData)
      return res.data.photo_urls
    },

    async myWeekDeletePhoto(_, { id, mediaId }) {
      const formData = new FormData()
      formData.append('photos_to_delete[0]', mediaId)
      const res = await Api.post(`/my-week-sheets/${id}/photos`, formData)
      return res.data.photo_urls
    },

    // The external supervisors the company admin added to this sheet's project.
    async myWeekSupervisors(_, id) {
      const res = await Api.get(`/my-week-sheets/${id}/supervisors`)
      return res.data.data || []
    },

    // A rejected (or still pending) sheet goes back through resubmit, which
    // cancels the old link; a draft is a first send.
    async myWeekSend(_, { id, supervisorId, resubmit = false }) {
      const res = await Api.post(
        `/my-week-sheets/${id}/${resubmit ? 'resubmit' : 'submit'}-external`,
        { external_supervisor_id: supervisorId },
      )
      return res.data
    },

    async myWeekResend(_, id) {
      const res = await Api.post(`/my-week-sheets/${id}/resend-link`)
      return res.data
    },

    async myWeekWithdraw(_, id) {
      const res = await Api.post(`/my-week-sheets/${id}/withdraw`)
      return res.data.data
    },
  },
}
